import React from 'react';
import './order-search-result.scss';
import {
  getSearchKey,
  OrderSearchParameter,
  Product,
  ProductVariantCategory,
  useAppSelector,
} from '@zonofi/zono-money-store';
import { Avatar, LoadingSpinner, ProductImage } from '../../components';
import { tagsColorScheme } from '../../constants';

interface OrderSearchResultProps {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  searchResults: any[];
  searchParameter?: OrderSearchParameter;
  onUpdateSearchParameter?: (
    paramId: string,
    value: string,
    updateSearchValue?: string | string[]
  ) => void;
  setShowSearchWindow?: (val: boolean) => void;
  isLoading: boolean;
}

export const OrderSearchResult: React.FC<OrderSearchResultProps> = ({
  searchResults,
  searchParameter = undefined,
  onUpdateSearchParameter = undefined,
  setShowSearchWindow = () => {},
  isLoading,
}) => {
  const searchKey = useAppSelector(getSearchKey);
  const { parameterId, value } = searchParameter!;

  const highlightMatchingText = (
    text: string,
    searchValue: string
  ): React.ReactNode => {
    if (!text || !searchValue) return text;

    // Create a case-insensitive regex to find matches
    const regex = new RegExp(`(${searchValue})`, 'gi');
    const parts = text.split(regex);

    return parts.map((part, index) => {
      // Check if this part matches the search value (case-insensitive)
      const isMatch = part.toLowerCase() === searchValue.toLowerCase();

      return isMatch ? (
        <span
          key={index}
          className="order-search-result-customers-customer-info-name font-bold"
        >
          {part}
        </span>
      ) : (
        <span className="order-search-result-customers-customer-info-name font-regular">
          {part}
        </span>
      );
    });
  };

  const renderTeamMembers = () => {
    return (
      <div className="order-search-result-wrapper">
        <div className="order-search-result-teamMembers">
          {searchResults.map((teamMember, idx, teamMembersArr) => (
            <>
              <div
                className="order-search-result-teamMembers-teamMember width-100"
                key={teamMember?.userId}
                onClick={() => {
                  onUpdateSearchParameter?.(
                    parameterId,
                    searchKey,
                    teamMember?.userId
                  );
                  setShowSearchWindow?.(false);
                }}
              >
                <Avatar title={teamMember?.name} size={28} />
                <div className="order-search-result-teamMembers-teamMember-info">
                  <span className="order-search-result-teamMembers-teamMember-info-name font-regular">
                    {highlightMatchingText(teamMember?.name, searchKey)}
                  </span>
                  <span className="order-search-result-teamMembers-teamMember-info-mobile font-regular">
                    {teamMember?.mobile}
                  </span>
                  <span className="order-search-result-teamMembers-teamMember-info-bullet font-regular">
                    •
                  </span>
                  <span className="order-search-result-teamMembers-teamMember-info-role font-regular">
                    {teamMember?.userType}
                  </span>
                </div>
              </div>
              {teamMembersArr?.length - 1 > idx && (
                <div className="order-search-result-teamMembers-divider" />
              )}
            </>
          ))}
        </div>
      </div>
    );
  };

  const renderProducts = () => {
    return (
      <div className="order-search-result-wrapper">
        <div className="order-search-result-products">
          {(searchResults as Product[]).map((product, idx, productsArr) => (
            <>
              <div
                className="order-search-result-products-product width-100"
                key={product?.id}
                onClick={() => {
                  onUpdateSearchParameter?.(parameterId, searchKey, [
                    product?.productVariants?.[0]?.productVariantId!.toString(),
                  ]);
                  setShowSearchWindow?.(false);
                }}
              >
                <div className="order-search-result-products-product-image">
                  <ProductImage
                    productImage={
                      product?.productVariants?.[0]?.assets?.[0]?.redirectUrl
                    }
                  />
                </div>
                <div className="order-search-result-products-product-info">
                  <span className="order-search-result-products-product-info-name font-semibold">
                    {product?.productVariants?.[0]?.name}
                  </span>
                  <span className="order-search-result-products-product-info-tags font-medium">
                    {product?.productVariants?.[0]?.categories?.map(
                      (category: ProductVariantCategory, idx, categories) => {
                        return idx <= 2 ? (
                          <div
                            className="order-search-result-products-product-info-tags-tag"
                            key={category?.categoryId}
                            style={{
                              backgroundColor:
                                tagsColorScheme?.[idx]?.backgroundColor,
                              color: tagsColorScheme?.[idx]?.color,
                            }}
                          >
                            <span className="order-search-result-products-product-info-tags-tag-text font-semibold">
                              {category?.categoryName}
                            </span>
                          </div>
                        ) : (
                          <div
                            className="order-search-result-products-product-info-tags-exrta"
                            style={{
                              backgroundColor: '#F0F0F0',
                              color: '#000000',
                            }}
                          >
                            <span className="order-search-result-products-product-info-tags-exrta-text font-medium">
                              +{categories?.length - 3}
                            </span>
                          </div>
                        );
                      }
                    )}
                  </span>
                </div>
              </div>
              {productsArr?.length - 1 > idx && (
                <div className="order-search-result-products-divider" />
              )}
            </>
          ))}
        </div>
      </div>
    );
  };

  const renderCustomers = () => {
    return (
      <div className="order-search-result-wrapper">
        <div className="order-search-result-customers">
          {searchResults.map((customer, idx, customersArr) => (
            <>
              <div
                className="order-search-result-customers-customer width-100"
                key={customer?.id}
                onClick={() => {
                  onUpdateSearchParameter?.(
                    parameterId,
                    searchKey,
                    customer?.id
                  );
                  setShowSearchWindow?.(false);
                }}
              >
                <Avatar title={customer?.name} size={28} />
                <div className="order-search-result-customers-customer-info">
                  <span className="order-search-result-customers-customer-info-name font-regular">
                    {highlightMatchingText(customer?.name, searchKey)}
                  </span>
                  <span className="order-search-result-customers-customer-info-mobile font-regular">
                    {customer?.mobile?.slice(2)}
                  </span>
                </div>
              </div>
              {customersArr?.length - 1 > idx && (
                <div className="order-search-result-customers-divider" />
              )}
            </>
          ))}
        </div>
      </div>
    );
  };

  const renderSearchResults = () => {
    switch (parameterId) {
      case 'customerId':
        return renderCustomers();
      case 'userId':
        return renderTeamMembers();
      case 'productVariantIds':
        return renderProducts();
      default:
        return null;
    }
  };

  return (
    <div className="order-search-result">
      <span className="order-search-result-label font-regular">
        {searchResults?.length} result{searchResults?.length > 1 ? 's' : ''}{' '}
        found for "<span className="font-bold">{value}</span>"
      </span>
      {isLoading ? <LoadingSpinner size={'small'} /> : renderSearchResults()}
    </div>
  );
};
